import { z } from "zod";
export const MAX_AGENT_SKILLS = 32;
// The control-plane body limit is 256 KiB; canonical YAML frontmatter adds a
// bounded name and JSON-escaped description before publication.
export const MAX_AGENT_SKILL_DOCUMENT_BYTES = 264 * 1024;
/**
 * The full snapshot is repeated in the persisted command, RuntimeStart, and
 * trace manifest's initial command. Keep its encoded form bounded so a
 * maximum conversation snapshot still fits the fixed 2 MiB launch envelope.
 */
export const MAX_AGENT_SKILL_CATALOG_SNAPSHOT_BYTES = 320 * 1024;
const sha256DigestSchema = z.string().regex(/^sha256:[a-f0-9]{64}$/);
const skillNameSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const skillCatalogUrlSchema = z
    .url()
    .refine(validCatalogUrl, "Skill catalog URL must be installation-owned HTTP(S)");
export const agentSkillCatalogEntrySchema = z
    .object({
    name: skillNameSchema,
    version: sha256DigestSchema,
    contentDigest: sha256DigestSchema,
    contentBytes: z.number().int().min(1).max(MAX_AGENT_SKILL_DOCUMENT_BYTES),
    content: z.string().min(1),
})
    .strict()
    .superRefine((entry, context) => {
    if (entry.version !== entry.contentDigest) {
        context.addIssue({
            code: "custom",
            path: ["version"],
            message: "Skill version must equal its content digest",
        });
    }
    if (agentSkillSha256(entry.content) !== entry.contentDigest) {
        context.addIssue({
            code: "custom",
            path: ["contentDigest"],
            message: "Skill content digest does not match its document",
        });
    }
    const contentBytes = utf8Bytes(entry.content);
    if (contentBytes !== entry.contentBytes) {
        context.addIssue({
            code: "custom",
            path: ["contentBytes"],
            message: "Skill content byte count does not match its document",
        });
    }
    if (contentBytes > MAX_AGENT_SKILL_DOCUMENT_BYTES) {
        context.addIssue({
            code: "custom",
            path: ["content"],
            message: "Skill document exceeds the runtime snapshot bound",
        });
    }
});
export const agentSkillCatalogSnapshotSchema = z
    .object({
    schemaVersion: z.literal(1),
    catalogUrl: skillCatalogUrlSchema,
    catalogDigest: sha256DigestSchema,
    skills: z.array(agentSkillCatalogEntrySchema).max(MAX_AGENT_SKILLS),
})
    .strict()
    .superRefine((snapshot, context) => {
    let previousName = null;
    for (let index = 0; index < snapshot.skills.length; index += 1) {
        const name = snapshot.skills[index].name;
        if (previousName !== null && name <= previousName) {
            context.addIssue({
                code: "custom",
                path: ["skills", index, "name"],
                message: "Skill snapshot entries must be unique and sorted by name",
            });
            break;
        }
        previousName = name;
    }
    if (utf8Bytes(canonicalAgentSkillCatalogSnapshot(snapshot)) >
        MAX_AGENT_SKILL_CATALOG_SNAPSHOT_BYTES) {
        context.addIssue({
            code: "custom",
            path: ["skills"],
            message: "Skill catalog snapshot exceeds the runtime launch bound",
        });
    }
    if (agentSkillSha256(canonicalAgentSkillCatalogSnapshot(snapshot)) !==
        snapshot.catalogDigest) {
        context.addIssue({
            code: "custom",
            path: ["catalogDigest"],
            message: "Skill catalog digest does not match its canonical snapshot",
        });
    }
});
export const agentSkillCatalogIdentitySchema = z
    .object({
    schemaVersion: z.literal(1),
    catalogUrl: skillCatalogUrlSchema,
    catalogDigest: sha256DigestSchema,
    skills: z
        .array(z
        .object({
        name: skillNameSchema,
        version: sha256DigestSchema,
        contentDigest: sha256DigestSchema,
        contentBytes: z
            .number()
            .int()
            .min(1)
            .max(MAX_AGENT_SKILL_DOCUMENT_BYTES),
    })
        .strict())
        .max(MAX_AGENT_SKILLS),
})
    .strict();
/** Stable semantic payload hashed by every producer and consumer. */
export function canonicalAgentSkillCatalogSnapshot(snapshot) {
    return JSON.stringify({
        schemaVersion: snapshot.schemaVersion,
        catalogUrl: snapshot.catalogUrl,
        skills: snapshot.skills.map((skill) => ({
            name: skill.name,
            version: skill.version,
            contentDigest: skill.contentDigest,
            contentBytes: skill.contentBytes,
            content: skill.content,
        })),
    });
}
export function agentSkillCatalogIdentity(snapshot) {
    return agentSkillCatalogIdentitySchema.parse({
        schemaVersion: snapshot.schemaVersion,
        catalogUrl: snapshot.catalogUrl,
        catalogDigest: snapshot.catalogDigest,
        skills: snapshot.skills.map(({ content: _content, ...skill }) => skill),
    });
}
/** Browser-safe synchronous SHA-256 used by strict protocol boundaries. */
export function agentSkillSha256(value) {
    const source = new TextEncoder().encode(value);
    const paddedLength = Math.ceil((source.byteLength + 9) / 64) * 64;
    const bytes = new Uint8Array(paddedLength);
    bytes.set(source);
    bytes[source.byteLength] = 0x80;
    const view = new DataView(bytes.buffer);
    const bitLength = source.byteLength * 8;
    view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x1_0000_0000));
    view.setUint32(paddedLength - 4, bitLength >>> 0);
    const state = new Uint32Array([
        0x6a09e667,
        0xbb67ae85,
        0x3c6ef372,
        0xa54ff53a,
        0x510e527f,
        0x9b05688c,
        0x1f83d9ab,
        0x5be0cd19,
    ]);
    const words = new Uint32Array(64);
    for (let offset = 0; offset < paddedLength; offset += 64) {
        for (let index = 0; index < 16; index += 1) {
            words[index] = view.getUint32(offset + index * 4);
        }
        for (let index = 16; index < 64; index += 1) {
            const prior15 = words[index - 15];
            const prior2 = words[index - 2];
            const sigma0 = rotateRight(prior15, 7) ^
                rotateRight(prior15, 18) ^
                (prior15 >>> 3);
            const sigma1 = rotateRight(prior2, 17) ^
                rotateRight(prior2, 19) ^
                (prior2 >>> 10);
            words[index] =
                (words[index - 16] + sigma0 + words[index - 7] + sigma1) >>> 0;
        }
        let a = state[0];
        let b = state[1];
        let c = state[2];
        let d = state[3];
        let e = state[4];
        let f = state[5];
        let g = state[6];
        let h = state[7];
        for (let index = 0; index < 64; index += 1) {
            const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
            const choice = (e & f) ^ (~e & g);
            const temporary1 = (h + sum1 + choice + SHA256_CONSTANTS[index] + words[index]) >>> 0;
            const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
            const majority = (a & b) ^ (a & c) ^ (b & c);
            const temporary2 = (sum0 + majority) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + temporary1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temporary1 + temporary2) >>> 0;
        }
        state[0] = (state[0] + a) >>> 0;
        state[1] = (state[1] + b) >>> 0;
        state[2] = (state[2] + c) >>> 0;
        state[3] = (state[3] + d) >>> 0;
        state[4] = (state[4] + e) >>> 0;
        state[5] = (state[5] + f) >>> 0;
        state[6] = (state[6] + g) >>> 0;
        state[7] = (state[7] + h) >>> 0;
    }
    return `sha256:${Array.from(state)
        .map((word) => word.toString(16).padStart(8, "0"))
        .join("")}`;
}
function utf8Bytes(value) {
    return new TextEncoder().encode(value).byteLength;
}
function rotateRight(value, bits) {
    return (value >>> bits) | (value << (32 - bits));
}
function validCatalogUrl(value) {
    try {
        const url = new URL(value);
        return ((url.protocol === "http:" || url.protocol === "https:") &&
            url.username === "" &&
            url.password === "" &&
            url.search === "" &&
            url.hash === "" &&
            url.pathname === "/.well-known/agent-skills/");
    }
    catch {
        return false;
    }
}
const SHA256_CONSTANTS = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
    0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
    0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
    0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
    0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
    0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
//# sourceMappingURL=skills.js.map